import axios from 'axios'

import { Env, readFromEnv } from '@cinch-nx/environments'
import { datadogRum } from '@datadog/browser-rum'
import { formatPostcode } from '../utils/postcode-helper'

export type AddressIoSuggestion = {
  address: string
  url: string
  id: string
}

type AddressIoSuggestionsResponse = {
  suggestions: AddressIoSuggestion[]
  data: { suggestions: AddressIoSuggestion[] }
}

type AddressIoAddressDetails = {
  postcode: string
  latitude: number
  longitude: number
  formatted_address: string[]
  thoroughfare: string
  building_name: string
  sub_building_name: string
  sub_building_number: string
  building_number: string
  line_1: string
  line_2: string
  line_3: string
  line_4: string
  locality: string
  town_or_city: string
  county: string
  district: string
  country: string
  residential: boolean
}

const getAddressIoService = axios.create({
  baseURL: readFromEnv(Env.GetAddressIoUrl),
  params: {
    'api-key': readFromEnv(Env.GetAddressIoApiKey),
    all: true,
  },
})

export const findAddresses = async (postcode: string) => {
  try {
    const response =
      await getAddressIoService.request<AddressIoSuggestionsResponse>({
        method: 'GET',
        url: `/autocomplete/${formatPostcode(postcode)}`,
      })

    if (!response || !response.data || !response.data.suggestions) {
      datadogRum.addError('Cinch Servicing: Find Addresses - No Results', {
        cinchServicing: true,
        apiEndpoint: '/autocomplete/{postcode}',
        postcode: formatPostcode(postcode),
        resultCount: 0,
      })
      return []
    }

    datadogRum.addAction('Cinch Servicing: Find Addresses', {
      cinchServicing: true,
      apiEndpoint: '/autocomplete/{postcode}',
      postcode: formatPostcode(postcode),
      resultCount: response.data.suggestions.length,
    })

    return response.data.suggestions
  } catch (error) {
    datadogRum.addError(new Error('Cinch Servicing: Find Addresses Error'), {
      cinchServicing: true,
      apiEndpoint: '/autocomplete/{postcode}',
      postcode: formatPostcode(postcode),
      error: error,
    })
    throw error
  }
}

export const getAddressDetails = async (addressId: string) => {
  try {
    const response = await getAddressIoService.request<AddressIoAddressDetails>(
      {
        method: 'GET',
        url: `/get/${addressId}`,
      },
    )

    if (!response || !response.data) {
      datadogRum.addAction('Cinch Servicing: Get Address Details - No Data', {
        cinchServicing: true,
        apiEndpoint: '/get/{addressId}',
        addressId,
      })
      return null
    }

    datadogRum.addAction('Cinch Servicing: Get Address Details', {
      cinchServicing: true,
      apiEndpoint: '/get/{addressId}',
      addressId,
      hasData: true,
    })

    return {
      addressLine1: response.data.line_1,
      addressLine2: response.data.line_2,
      postTown: response.data.town_or_city,
      county: response.data.county,
      postcode: response.data.postcode,
    }
  } catch (error) {
    datadogRum.addError(
      new Error('Cinch Servicing: Get Address Details Error'),
      {
        cinchServicing: true,
        apiEndpoint: '/get/{addressId}',
        addressId,
        error: error,
      },
    )
    return null
  }
}
